import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Select,
  Row,
  Col,
  message,
  Tag,
  Spin,
  Icon,
  Input,
  Tabs,
  InputNumber,
  Popconfirm,
  Checkbox,
  Alert,
  Progress,
  Tooltip,
} from 'antd';
import moment from 'moment';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Papa from 'papaparse';
import { apiGet } from './utils/apiService';

const { Option } = Select;
const { TabPane } = Tabs;

const DIMENSION_STORAGE_KEY = 'space_util_dimensions';
const RENT_STORAGE_KEY = 'space_util_parking_rent';

const TO_CM = { cm: 1, inches: 2.54, ft: 30.48 };

const SQ_CM_TO_DISPLAY = {
  sq_cm: 1,
  sq_m: 1 / 10000,
  sq_ft: 1 / 929.0304,
  sq_inches: 1 / 6.4516,
};

const DISPLAY_UNIT_LABELS = {
  sq_cm: 'sq cm',
  sq_m: 'sq m',
  sq_ft: 'sq ft',
  sq_inches: 'sq in',
};

const LEVEL_NAMES = ['location', 'subLocation', 'assetType', 'assetSubType', 'model', 'owner'];
const LEVEL_COLORS = {
  location: 'blue',
  subLocation: 'cyan',
  assetType: 'green',
  assetSubType: 'lime',
  model: 'gold',
  owner: 'default',
};
const LEVEL_LABELS = {
  location: 'Location',
  subLocation: 'Sub-Location',
  assetType: 'Asset Type',
  assetSubType: 'Sub-Type',
  model: 'Model',
  owner: 'Owner',
};

const toCm = (value, unit) => value * (TO_CM[unit] || 1);

const calcAreaSqCm = (dim) => {
  if (!dim || !dim.length || !dim.breadth) return 0;
  return toCm(dim.length, dim.unit) * toCm(dim.breadth, dim.unit);
};

const convertArea = (areaSqCm, displayUnit) => areaSqCm * (SQ_CM_TO_DISPLAY[displayUnit] || 1);

const fmtArea = (areaSqCm, displayUnit) => {
  if (!areaSqCm) return '0.00';
  return convertArea(areaSqCm, displayUnit).toFixed(2);
};

const extractName = (field) => {
  if (!field) return null;
  if (typeof field === 'string') return field.trim();
  if (typeof field === 'object') return field.name || field.code || field.title || null;
  return null;
};

const extractId = (field) => {
  if (!field) return null;
  if (typeof field === 'string') return field;
  if (typeof field === 'object') return field._id || field.id || null;
  return null;
};

const loadDimensions = () => {
  try {
    const raw = localStorage.getItem(DIMENSION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    console.error('Failed to load dimensions:', e);
    return {};
  }
};

const saveDimensions = (dims) => {
  try {
    localStorage.setItem(DIMENSION_STORAGE_KEY, JSON.stringify(dims));
  } catch (e) {
    console.error('Failed to save dimensions:', e);
    message.error('Failed to save dimensions — storage may be full');
  }
};

const loadRent = () => {
  try {
    const raw = localStorage.getItem(RENT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    console.error('Failed to load rent data:', e);
    return {};
  }
};

const saveRent = (data) => {
  try {
    localStorage.setItem(RENT_STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.error('Failed to save rent data:', e);
    message.error('Failed to save rent data — storage may be full');
  }
};

const rentKey = (loc, subLoc) => `${loc}__${subLoc}`;

const uniq = (arr) => [...new Set(arr.filter((v) => v && v !== '-'))].sort();

function SpaceUtilizationReport() {
  const [allAssets, setAllAssets] = useState([]);
  const [allModels, setAllModels] = useState([]);
  const [lookups, setLookups] = useState({ locationById: {}, clientById: {}, typeById: {}, subtypeById: {}, ownerById: {} });
  const [loading, setLoading] = useState(false);

  const [dimensions, setDimensions] = useState(loadDimensions);
  const [parkingRent, setParkingRent] = useState(loadRent);
  const [editingRentKey, setEditingRentKey] = useState(null);
  const [editRentAmount, setEditRentAmount] = useState(null);
  const [rentFilterSearch, setRentFilterSearch] = useState('');
  const [dimFilterType, setDimFilterType] = useState(null);
  const [dimFilterSubType, setDimFilterSubType] = useState(null);
  const [dimFilterSearch, setDimFilterSearch] = useState('');
  const [bulkLength, setBulkLength] = useState(null);
  const [bulkBreadth, setBulkBreadth] = useState(null);
  const [bulkUnit, setBulkUnit] = useState('cm');
  const [editingModelId, setEditingModelId] = useState(null);
  const [editLength, setEditLength] = useState(null);
  const [editBreadth, setEditBreadth] = useState(null);
  const [editUnit, setEditUnit] = useState('cm');

  const [fLocation, setFLocation] = useState(null);
  const [fSubLocation, setFSubLocation] = useState(null);
  const [fAssetType, setFAssetType] = useState(null);
  const [fAssetSubType, setFAssetSubType] = useState(null);
  const [fModel, setFModel] = useState(null);
  const [fOwner, setFOwner] = useState(null);
  const [displayUnit, setDisplayUnit] = useState('sq_ft');
  const [onlyWithRent, setOnlyWithRent] = useState(false);
  const [filtersVisible, setFiltersVisible] = useState(true);

  const [activeTab, setActiveTab] = useState('dimensions');

  // Fetch data on mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const opts = { headers: { Accept: '*/*' } };
        const [assetRes, modelRes, locRes, clientRes, typeRes, subRes, ownerRes] = await Promise.all([
          apiGet('/asset/asset-all', opts),
          apiGet('/asset-model', opts),
          apiGet('/location', opts),
          apiGet('/client', opts),
          apiGet('/asset-type', opts),
          apiGet('/equipment-sub-type', opts),
          apiGet('/owner?limit=10000&offset=0&filters=[]', opts),
        ]);

        const extract = (res) => {
          if (!res || res.success === false) return [];
          const d = res.data;
          if (Array.isArray(d)) return d;
          if (d && Array.isArray(d.data)) return d.data;
          if (d && Array.isArray(d.models)) return d.models;
          if (d && Array.isArray(d.subTypes)) return d.subTypes;
          if (d && Array.isArray(d.assets)) return d.assets;
          if (d && Array.isArray(d.owners)) return d.owners;
          if (d && Array.isArray(d.rows)) return d.rows;
          if (d && Array.isArray(d.results)) return d.results;
          return [];
        };

        const assets = extract(assetRes);
        const models = extract(modelRes);
        const locations = extract(locRes);
        const clients = extract(clientRes);
        const types = extract(typeRes);
        const subtypes = extract(subRes);
        const owners = extract(ownerRes);

        const locationById = {};
        locations.forEach((l) => { locationById[l._id] = l.name || l.code || ''; });
        const clientById = {};
        clients.forEach((c) => { clientById[c._id] = c.name || c.code || ''; });
        const typeById = {};
        types.forEach((t) => { typeById[t._id] = t.name || t.code || ''; });
        const subtypeById = {};
        subtypes.forEach((s) => { subtypeById[s._id] = s; });
        const ownerById = {};
        owners.forEach((o) => { ownerById[o._id] = o.name || o.code || ''; });

        setAllAssets(assets);
        setAllModels(models);
        setLookups({ locationById, clientById, typeById, subtypeById, ownerById });
      } catch (e) {
        console.error('Failed to load data:', e);
        message.error('Failed to load asset data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Build model lookup maps
  const modelNameById = useMemo(() => {
    const map = {};
    allModels.forEach((m) => { map[m._id] = m.name || m.code || ''; });
    return map;
  }, [allModels]);

  // Enrich models with resolved type/sub-type names for dimension setup filtering
  const enrichedModels = useMemo(() => {
    const { typeById, subtypeById } = lookups;
    return allModels.map((m) => {
      const subId = typeof m.assetSubType === 'string' ? m.assetSubType : (m.assetSubType && m.assetSubType._id) || '';
      const sub = subtypeById[subId] || (typeof m.assetSubType === 'object' ? m.assetSubType : null);
      const subTypeName = sub ? (sub.name || sub.code || '') : '';
      const typeId = sub ? (typeof sub.assetType === 'string' ? sub.assetType : (sub.assetType && sub.assetType._id) || '') : '';
      const typeName = typeId ? (typeById[typeId] || '') : '';
      return { ...m, _assetTypeName: typeName, _assetSubTypeName: subTypeName };
    });
  }, [allModels, lookups]);

  // Filter options for dimension setup
  const dimTypeOptions = useMemo(() => uniq(enrichedModels.map((m) => m._assetTypeName)), [enrichedModels]);
  const dimSubTypeOptions = useMemo(
    () => uniq(enrichedModels.filter((m) => !dimFilterType || m._assetTypeName === dimFilterType).map((m) => m._assetSubTypeName)),
    [enrichedModels, dimFilterType]
  );

  // Filtered models for dimension setup table
  const filteredDimModels = useMemo(() => {
    const search = (dimFilterSearch || '').toLowerCase().trim();
    return enrichedModels.filter((m) => {
      if (dimFilterType && m._assetTypeName !== dimFilterType) return false;
      if (dimFilterSubType && m._assetSubTypeName !== dimFilterSubType) return false;
      if (search) {
        const name = (m.name || '').toLowerCase();
        const code = (m.code || '').toLowerCase();
        if (!name.includes(search) && !code.includes(search)) return false;
      }
      return true;
    });
  }, [enrichedModels, dimFilterType, dimFilterSubType, dimFilterSearch]);

  // Resolve a ref field: could be a populated object, a string ID, or null
  const resolveName = (field, lookupMap) => {
    if (!field) return null;
    if (typeof field === 'object') return field.name || field.code || field.title || null;
    if (typeof field === 'string' && lookupMap) return lookupMap[field] || null;
    return null;
  };

  // Normalize assets
  const normalizedAssets = useMemo(() => {
    const { locationById, clientById, typeById, subtypeById, ownerById } = lookups;
    return allAssets.map((a) => {
      const modelId = extractId(a.assetModel);
      let modelName = extractName(a.assetModel);
      if (!modelName && modelId && modelNameById[modelId]) {
        modelName = modelNameById[modelId];
      }

      // Resolve location
      const locationName = resolveName(a.location, locationById) || 'Unassigned';

      // Resolve sub-location (could be subLocation or client field)
      const subLocationName = resolveName(a.subLocation, clientById) || resolveName(a.client, clientById) || 'Unassigned';

      // Resolve asset type — from equipmentSubType.assetType or assetType field
      let assetTypeName = 'Unknown';
      if (a.equipmentSubType && typeof a.equipmentSubType === 'object') {
        assetTypeName = resolveName(a.equipmentSubType.assetType, typeById) || 'Unknown';
      } else if (a.equipmentSubType && typeof a.equipmentSubType === 'string') {
        const sub = subtypeById[a.equipmentSubType];
        if (sub) assetTypeName = resolveName(sub.assetType, typeById) || 'Unknown';
      } else {
        assetTypeName = resolveName(a.assetType, typeById) || 'Unknown';
      }

      // Resolve asset sub-type
      let assetSubTypeName = 'Unknown';
      if (a.equipmentSubType && typeof a.equipmentSubType === 'object') {
        assetSubTypeName = a.equipmentSubType.name || a.equipmentSubType.code || 'Unknown';
      } else if (a.equipmentSubType && typeof a.equipmentSubType === 'string') {
        const sub = subtypeById[a.equipmentSubType];
        assetSubTypeName = sub ? (sub.name || sub.code || 'Unknown') : 'Unknown';
      } else if (a.assetSubType) {
        assetSubTypeName = resolveName(a.assetSubType, subtypeById) || 'Unknown';
      }

      // Resolve owner name from owner ID
      const ownerId = extractId(a.owner);
      const ownerName = resolveName(a.owner, ownerById) || a.ownerCode || 'Unassigned';

      return {
        assetCode: a.assetCode || '',
        ownerCode: a.ownerCode || '',
        ownerName,
        locationName,
        subLocationName,
        assetTypeName,
        assetSubTypeName,
        assetModelName: modelName || 'Unknown',
        assetModelId: modelId,
      };
    });
  }, [allAssets, modelNameById, lookups]);

  // Count assets per model (for dimension setup tab)
  const assetCountByModelId = useMemo(() => {
    const counts = {};
    normalizedAssets.forEach((a) => {
      if (a.assetModelId) {
        counts[a.assetModelId] = (counts[a.assetModelId] || 0) + 1;
      }
    });
    return counts;
  }, [normalizedAssets]);

  // Cascading filter options
  const filteredAssets = useMemo(() => {
    return normalizedAssets.filter((a) => {
      if (fLocation && a.locationName !== fLocation) return false;
      if (fSubLocation && a.subLocationName !== fSubLocation) return false;
      if (fAssetType && a.assetTypeName !== fAssetType) return false;
      if (fAssetSubType && a.assetSubTypeName !== fAssetSubType) return false;
      if (fModel && a.assetModelName !== fModel) return false;
      if (fOwner && a.ownerName !== fOwner) return false;
      if (onlyWithRent) {
        const rk = rentKey(a.locationName, a.subLocationName);
        if (!parkingRent[rk] || !parkingRent[rk].rent || parkingRent[rk].rent <= 0) return false;
      }
      return true;
    });
  }, [normalizedAssets, fLocation, fSubLocation, fAssetType, fAssetSubType, fModel, fOwner, onlyWithRent, parkingRent]);

  const locationOptions = useMemo(() => uniq(normalizedAssets.map((a) => a.locationName)), [normalizedAssets]);
  const subLocationOptions = useMemo(
    () => uniq(normalizedAssets.filter((a) => !fLocation || a.locationName === fLocation).map((a) => a.subLocationName)),
    [normalizedAssets, fLocation]
  );
  const assetTypeOptions = useMemo(() => uniq(normalizedAssets.map((a) => a.assetTypeName)), [normalizedAssets]);
  const assetSubTypeOptions = useMemo(
    () => uniq(normalizedAssets.filter((a) => !fAssetType || a.assetTypeName === fAssetType).map((a) => a.assetSubTypeName)),
    [normalizedAssets, fAssetType]
  );
  const modelOptions = useMemo(
    () => uniq(normalizedAssets
      .filter((a) => !fAssetType || a.assetTypeName === fAssetType)
      .filter((a) => !fAssetSubType || a.assetSubTypeName === fAssetSubType)
      .map((a) => a.assetModelName)),
    [normalizedAssets, fAssetType, fAssetSubType]
  );
  const ownerOptions = useMemo(() => uniq(filteredAssets.map((a) => a.ownerName)), [filteredAssets]);

  const resetFilters = () => {
    setFLocation(null);
    setFSubLocation(null);
    setFAssetType(null);
    setFAssetSubType(null);
    setFModel(null);
    setFOwner(null);
    setOnlyWithRent(false);
  };

  // Build hierarchical report data
  const reportData = useMemo(() => {
    const tree = {};
    filteredAssets.forEach((a) => {
      if (!tree[a.locationName]) tree[a.locationName] = {};
      const loc = tree[a.locationName];
      if (!loc[a.subLocationName]) loc[a.subLocationName] = {};
      const sub = loc[a.subLocationName];
      if (!sub[a.assetTypeName]) sub[a.assetTypeName] = {};
      const typ = sub[a.assetTypeName];
      if (!typ[a.assetSubTypeName]) typ[a.assetSubTypeName] = {};
      const styp = typ[a.assetSubTypeName];
      if (!styp[a.assetModelName]) styp[a.assetModelName] = {};
      const mod = styp[a.assetModelName];
      const ownerKey = a.ownerName || 'Unassigned';
      if (!mod[ownerKey]) mod[ownerKey] = { assets: [], modelId: a.assetModelId };
      mod[ownerKey].assets.push(a);
    });

    const buildNodes = (group, depth, parentKey, ctx) => {
      return Object.entries(group)
        .map(([name, child]) => {
          const key = `${parentKey}_${name}`;
          const level = LEVEL_NAMES[depth];
          const nextCtx = { ...ctx };
          if (depth === 0) nextCtx.locationName = name;
          if (depth === 1) nextCtx.subLocationName = name;

          if (depth === 5) {
            const count = child.assets.length;
            const dim = dimensions[child.modelId];
            const individualSqCm = calcAreaSqCm(dim);
            const totalSqCm = count * individualSqCm;
            return {
              key,
              groupLabel: name,
              level,
              assetCount: count,
              individualAreaSqCm: individualSqCm,
              totalAreaSqCm: totalSqCm,
              dimensionStatus: dim && dim.length > 0 && dim.breadth > 0 ? 'set' : 'missing',
              dimensionText: dim && dim.length > 0 && dim.breadth > 0 ? `${dim.length} x ${dim.breadth} ${dim.unit}` : null,
              modelId: child.modelId,
              rent: null,
              ratePerSqFt: null,
            };
          }

          const children = buildNodes(child, depth + 1, key, nextCtx);
          const totalSqCm = children.reduce((sum, c) => sum + c.totalAreaSqCm, 0);
          const totalCount = children.reduce((sum, c) => sum + c.assetCount, 0);
          const hasMissing = children.some((c) => c.dimensionStatus === 'missing' || c.dimensionStatus === 'partial');

          let rent = null;
          let ratePerSqFt = null;
          if (depth === 1) {
            const rk = rentKey(nextCtx.locationName, name);
            const rentData = parkingRent[rk];
            if (rentData && rentData.rent > 0) {
              rent = rentData.rent;
              const totalSqFt = totalSqCm * SQ_CM_TO_DISPLAY['sq_ft'];
              ratePerSqFt = totalSqFt > 0 ? rent / totalSqFt : null;
            }
          }
          if (depth === 0) {
            rent = children.reduce((sum, c) => sum + (c.rent || 0), 0) || null;
            const totalSqFt = totalSqCm * SQ_CM_TO_DISPLAY['sq_ft'];
            ratePerSqFt = (rent && totalSqFt > 0) ? rent / totalSqFt : null;
          }

          return {
            key,
            groupLabel: name,
            level,
            assetCount: totalCount,
            individualAreaSqCm: null,
            totalAreaSqCm: totalSqCm,
            dimensionStatus: hasMissing ? 'partial' : 'set',
            dimensionText: null,
            children,
            rent,
            ratePerSqFt,
          };
        })
        .sort((a, b) => a.groupLabel.localeCompare(b.groupLabel));
    };

    return buildNodes(tree, 0, 'root', {});
  }, [filteredAssets, dimensions, parkingRent]);

  // Summary stats
  const summaryStats = useMemo(() => {
    const modelIdsInReport = new Set();
    const modelIdsWithDims = new Set();
    filteredAssets.forEach((a) => {
      if (a.assetModelId) {
        modelIdsInReport.add(a.assetModelId);
        if (dimensions[a.assetModelId] && dimensions[a.assetModelId].length > 0 && dimensions[a.assetModelId].breadth > 0) {
          modelIdsWithDims.add(a.assetModelId);
        }
      }
    });
    const totalSqCm = reportData.reduce((sum, n) => sum + n.totalAreaSqCm, 0);
    return {
      totalAssets: filteredAssets.length,
      totalArea: fmtArea(totalSqCm, displayUnit),
      modelsInReport: modelIdsInReport.size,
      modelsWithDims: modelIdsWithDims.size,
      modelsMissing: modelIdsInReport.size - modelIdsWithDims.size,
    };
  }, [filteredAssets, dimensions, reportData, displayUnit]);

  // Dimension setup stats
  const dimSetupStats = useMemo(() => {
    const total = allModels.length;
    const withDims = allModels.filter((m) => dimensions[m._id] && dimensions[m._id].length > 0 && dimensions[m._id].breadth > 0).length;
    return { total, withDims, missing: total - withDims };
  }, [allModels, dimensions]);

  // Build unique location + sub-location combos from all assets
  const locationSubLocationList = useMemo(() => {
    const seen = new Set();
    const list = [];
    normalizedAssets.forEach((a) => {
      const k = rentKey(a.locationName, a.subLocationName);
      if (!seen.has(k)) {
        seen.add(k);
        list.push({ locationName: a.locationName, subLocationName: a.subLocationName, key: k });
      }
    });
    return list.sort((a, b) => a.locationName.localeCompare(b.locationName) || a.subLocationName.localeCompare(b.subLocationName));
  }, [normalizedAssets]);

  const filteredRentList = useMemo(() => {
    const search = (rentFilterSearch || '').toLowerCase().trim();
    if (!search) return locationSubLocationList;
    return locationSubLocationList.filter((r) =>
      r.locationName.toLowerCase().includes(search) || r.subLocationName.toLowerCase().includes(search)
    );
  }, [locationSubLocationList, rentFilterSearch]);

  const rentStats = useMemo(() => {
    const total = locationSubLocationList.length;
    const withRent = locationSubLocationList.filter((r) => parkingRent[r.key] && parkingRent[r.key].rent > 0).length;
    return { total, withRent, missing: total - withRent };
  }, [locationSubLocationList, parkingRent]);

  // ---- Parking Rent Handlers ----
  const handleEditRent = (rk) => {
    setEditingRentKey(rk);
    const existing = parkingRent[rk];
    setEditRentAmount(existing ? existing.rent : null);
  };

  const handleSaveRent = () => {
    if (!editRentAmount || editRentAmount <= 0) {
      message.warning('Rent must be a positive number');
      return;
    }
    const updated = { ...parkingRent, [editingRentKey]: { rent: editRentAmount } };
    setParkingRent(updated);
    saveRent(updated);
    setEditingRentKey(null);
    message.success('Parking rent saved');
  };

  const handleCancelRentEdit = () => {
    setEditingRentKey(null);
  };

  const handleDeleteRent = (rk) => {
    const updated = { ...parkingRent };
    delete updated[rk];
    setParkingRent(updated);
    saveRent(updated);
    message.success('Parking rent removed');
  };

  const handleExportRent = () => {
    const rows = locationSubLocationList
      .filter((r) => parkingRent[r.key] && parkingRent[r.key].rent > 0)
      .map((r) => ({
        location: r.locationName,
        sub_location: r.subLocationName,
        monthly_rent: parkingRent[r.key].rent,
      }));
    if (rows.length === 0) {
      message.warning('No rent data to export');
      return;
    }
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', `Parking_Rent_${moment().format('YYYY-MM-DD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success(`Exported rent for ${rows.length} location(s)`);
  };

  const handleImportRent = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (!results.data || results.data.length === 0) {
          message.error('No data found in CSV');
          return;
        }
        const updated = { ...parkingRent };
        let imported = 0;
        let skipped = 0;
        results.data.forEach((row) => {
          const loc = (row.location || '').trim();
          const subLoc = (row.sub_location || '').trim();
          const rent = parseFloat(row.monthly_rent);
          if (!loc || !rent || rent <= 0) { skipped++; return; }
          const k = rentKey(loc, subLoc || 'Unassigned');
          updated[k] = { rent };
          imported++;
        });
        setParkingRent(updated);
        saveRent(updated);
        message.success(`Imported ${imported} rent record(s)${skipped > 0 ? `, skipped ${skipped} invalid row(s)` : ''}`);
      },
      error: () => { message.error('Failed to parse CSV file'); },
    });
    e.target.value = '';
  };

  // ---- Dimension Setup Handlers ----
  const handleEditDimension = (modelId) => {
    const dim = dimensions[modelId];
    setEditingModelId(modelId);
    setEditLength(dim ? dim.length : null);
    setEditBreadth(dim ? dim.breadth : null);
    setEditUnit(dim ? dim.unit : 'cm');
  };

  const handleSaveDimension = () => {
    if (!editLength || editLength <= 0 || !editBreadth || editBreadth <= 0) {
      message.warning('Length and breadth must be positive numbers');
      return;
    }
    const updated = { ...dimensions, [editingModelId]: { length: editLength, breadth: editBreadth, unit: editUnit } };
    setDimensions(updated);
    saveDimensions(updated);
    setEditingModelId(null);
    message.success('Dimensions saved');
  };

  const handleCancelEdit = () => {
    setEditingModelId(null);
  };

  const handleDeleteDimension = (modelId) => {
    const updated = { ...dimensions };
    delete updated[modelId];
    setDimensions(updated);
    saveDimensions(updated);
    message.success('Dimensions removed');
  };

  const handleBulkApply = () => {
    if (!bulkLength || bulkLength <= 0 || !bulkBreadth || bulkBreadth <= 0) {
      message.warning('Length and breadth must be positive numbers');
      return;
    }
    const updated = { ...dimensions };
    let count = 0;
    filteredDimModels.forEach((m) => {
      updated[m._id] = { length: bulkLength, breadth: bulkBreadth, unit: bulkUnit };
      count++;
    });
    setDimensions(updated);
    saveDimensions(updated);
    message.success(`Dimensions applied to ${count} model(s)`);
  };

  // ---- Dimension CSV Export/Import ----
  const handleExportDimensions = () => {
    const rows = enrichedModels
      .filter((m) => dimensions[m._id])
      .map((m) => {
        const dim = dimensions[m._id];
        return {
          model_id: m._id,
          model_name: m.name || '',
          model_code: m.code || '',
          asset_type: m._assetTypeName || '',
          asset_sub_type: m._assetSubTypeName || '',
          length: dim.length,
          breadth: dim.breadth,
          unit: dim.unit,
        };
      });
    if (rows.length === 0) {
      message.warning('No dimensions to export');
      return;
    }
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', `Asset_Model_Dimensions_${moment().format('YYYY-MM-DD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success(`Exported dimensions for ${rows.length} model(s)`);
  };

  const handleImportDimensions = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (!results.data || results.data.length === 0) {
          message.error('No data found in CSV');
          return;
        }
        const validUnits = ['cm', 'inches', 'ft'];
        const updated = { ...dimensions };
        let imported = 0;
        let skipped = 0;
        const modelIdSet = new Set(allModels.map((m) => m._id));
        // Also build a lookup by name and code for flexible matching
        const modelByName = {};
        const modelByCode = {};
        allModels.forEach((m) => {
          if (m.name) modelByName[m.name.trim().toLowerCase()] = m._id;
          if (m.code) modelByCode[m.code.trim().toLowerCase()] = m._id;
        });

        results.data.forEach((row) => {
          const length = parseFloat(row.length);
          const breadth = parseFloat(row.breadth);
          const unit = (row.unit || '').trim().toLowerCase();
          if (!length || length <= 0 || !breadth || breadth <= 0 || !validUnits.includes(unit)) {
            skipped++;
            return;
          }
          // Resolve model ID: try model_id first, then match by name or code
          let modelId = (row.model_id || '').trim();
          if (!modelId || !modelIdSet.has(modelId)) {
            const name = (row.model_name || '').trim().toLowerCase();
            const code = (row.model_code || '').trim().toLowerCase();
            modelId = (name && modelByName[name]) || (code && modelByCode[code]) || null;
          }
          if (!modelId) {
            skipped++;
            return;
          }
          updated[modelId] = { length, breadth, unit };
          imported++;
        });

        setDimensions(updated);
        saveDimensions(updated);
        message.success(`Imported ${imported} model(s)${skipped > 0 ? `, skipped ${skipped} invalid row(s)` : ''}`);
      },
      error: (err) => {
        console.error('CSV parse error:', err);
        message.error('Failed to parse CSV file');
      },
    });
    e.target.value = '';
  };

  // ---- Report Export Handlers ----
  const flattenForExport = (nodes, depth) => {
    const rows = [];
    (nodes || []).forEach((node) => {
      const indent = '  '.repeat(depth);
      rows.push({
        level: LEVEL_LABELS[node.level] || node.level,
        groupLabel: `${indent}${node.groupLabel}`,
        assetCount: node.assetCount,
        individualArea: node.individualAreaSqCm != null ? fmtArea(node.individualAreaSqCm, displayUnit) : '-',
        totalArea: fmtArea(node.totalAreaSqCm, displayUnit),
        dimensionText: node.dimensionText || '-',
        status: node.dimensionStatus === 'set' ? 'OK' : node.dimensionStatus === 'partial' ? 'Partial' : 'Missing',
        rent: node.rent != null ? node.rent : '-',
        ratePerSqFt: node.ratePerSqFt != null ? node.ratePerSqFt.toFixed(2) : '-',
      });
      if (node.children) {
        rows.push(...flattenForExport(node.children, depth + 1));
      }
    });
    return rows;
  };

  const handleDownloadCSV = () => {
    const unitLabel = DISPLAY_UNIT_LABELS[displayUnit];
    const headers = ['Level', 'Group', 'Asset Count', `Individual Area (${unitLabel})`, `Total Area (${unitLabel})`, 'Dimensions', 'Status', 'Monthly Rent', 'Rate / sq ft'];
    const flatRows = flattenForExport(reportData, 0);
    const csvContent = [
      headers.join(','),
      ...flatRows.map((r) => [r.level, r.groupLabel, r.assetCount, r.individualArea, r.totalArea, r.dimensionText, r.status, r.rent, r.ratePerSqFt].map((c) => `"${c}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', `Space_Utilization_Report_${moment().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success('CSV downloaded');
  };

  const handleDownloadPDF = () => {
    const unitLabel = DISPLAY_UNIT_LABELS[displayUnit];
    const doc = new jsPDF('landscape', 'pt', 'a4');
    doc.setFontSize(16);
    doc.text('Space Utilization Report', 30, 30);
    doc.setFontSize(10);
    doc.text(`Generated: ${moment().format('DD-MMM-YYYY HH:mm')}`, 30, 48);
    doc.text(`Total Assets: ${summaryStats.totalAssets} | Total Area: ${summaryStats.totalArea} ${unitLabel} | Display Unit: ${unitLabel}`, 30, 62);

    const flatRows = flattenForExport(reportData, 0);
    doc.autoTable({
      startY: 80,
      head: [['Level', 'Group', 'Count', `Area/Unit (${unitLabel})`, `Total Area (${unitLabel})`, 'Dimensions', 'Status', 'Monthly Rent', 'Rate/sq ft']],
      body: flatRows.map((r) => [r.level, r.groupLabel, r.assetCount, r.individualArea, r.totalArea, r.dimensionText, r.status, r.rent, r.ratePerSqFt]),
      styles: { fontSize: 7, cellPadding: 3 },
      headStyles: { fillColor: [41, 128, 185] },
      didParseCell: (data) => {
        if (data.section === 'body' && flatRows[data.row.index]) {
          const lvl = flatRows[data.row.index].level;
          if (lvl !== 'Owner') {
            data.cell.styles.fontStyle = 'bold';
          }
          if (flatRows[data.row.index].status === 'Missing') {
            if (data.column.index === 6) {
              data.cell.styles.textColor = [255, 77, 79];
            }
          }
        }
      },
    });

    doc.save(`Space_Utilization_Report_${moment().format('YYYY-MM-DD_HH-mm-ss')}.pdf`);
    message.success('PDF downloaded');
  };

  // ---- Dimension Setup Columns ----
  const dimColumns = [
    {
      title: 'Model Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Model Code',
      dataIndex: 'code',
      key: 'code',
      width: 100,
    },
    {
      title: 'Asset Type',
      dataIndex: '_assetTypeName',
      key: '_assetTypeName',
      width: 150,
      render: (text) => text || <span style={{ color: '#bbb' }}>-</span>,
    },
    {
      title: 'Sub-Type',
      dataIndex: '_assetSubTypeName',
      key: '_assetSubTypeName',
      width: 150,
      render: (text) => text || <span style={{ color: '#bbb' }}>-</span>,
    },
    {
      title: 'Assets',
      key: 'assetCount',
      width: 80,
      sorter: (a, b) => (assetCountByModelId[a._id] || 0) - (assetCountByModelId[b._id] || 0),
      render: (_, record) => assetCountByModelId[record._id] || 0,
    },
    {
      title: 'Length',
      key: 'length',
      width: 100,
      render: (_, record) => {
        if (editingModelId === record._id) {
          return <InputNumber min={0.01} step={0.1} value={editLength} onChange={setEditLength} style={{ width: 80 }} />;
        }
        const dim = dimensions[record._id];
        return dim ? dim.length : <span style={{ color: '#bbb' }}>-</span>;
      },
    },
    {
      title: 'Breadth',
      key: 'breadth',
      width: 100,
      render: (_, record) => {
        if (editingModelId === record._id) {
          return <InputNumber min={0.01} step={0.1} value={editBreadth} onChange={setEditBreadth} style={{ width: 80 }} />;
        }
        const dim = dimensions[record._id];
        return dim ? dim.breadth : <span style={{ color: '#bbb' }}>-</span>;
      },
    },
    {
      title: 'Unit',
      key: 'unit',
      width: 100,
      render: (_, record) => {
        if (editingModelId === record._id) {
          return (
            <Select value={editUnit} onChange={setEditUnit} style={{ width: 80 }}>
              <Option value="cm">cm</Option>
              <Option value="inches">inches</Option>
              <Option value="ft">ft</Option>
            </Select>
          );
        }
        const dim = dimensions[record._id];
        return dim ? dim.unit : <span style={{ color: '#bbb' }}>-</span>;
      },
    },
    {
      title: `Area (${DISPLAY_UNIT_LABELS[displayUnit]})`,
      key: 'area',
      width: 120,
      sorter: (a, b) => calcAreaSqCm(dimensions[a._id]) - calcAreaSqCm(dimensions[b._id]),
      render: (_, record) => {
        const dim = dimensions[record._id];
        if (!dim || !dim.length || !dim.breadth) return <span style={{ color: '#bbb' }}>-</span>;
        return fmtArea(calcAreaSqCm(dim), displayUnit);
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => {
        if (editingModelId === record._id) {
          return (
            <span>
              <Button type="primary" size="small" icon="save" onClick={handleSaveDimension} style={{ marginRight: 4 }}>Save</Button>
              <Button size="small" onClick={handleCancelEdit}>Cancel</Button>
            </span>
          );
        }
        const hasDim = dimensions[record._id] && dimensions[record._id].length > 0;
        return (
          <span>
            <Button type="link" size="small" icon="edit" onClick={() => handleEditDimension(record._id)}>
              {hasDim ? 'Edit' : 'Set'}
            </Button>
            {hasDim && (
              <Popconfirm title="Remove dimensions for this model?" onConfirm={() => handleDeleteDimension(record._id)}>
                <Button type="link" size="small" icon="delete" style={{ color: '#ff4d4f' }} />
              </Popconfirm>
            )}
          </span>
        );
      },
    },
  ];

  // ---- Report Table Columns ----
  const unitLabel = DISPLAY_UNIT_LABELS[displayUnit];
  const reportColumns = [
    {
      title: 'Group',
      dataIndex: 'groupLabel',
      key: 'groupLabel',
      width: 280,
      render: (text, record) => (
        <span style={{ fontWeight: record.level !== 'owner' ? 600 : 400 }}>{text}</span>
      ),
    },
    {
      title: 'Level',
      dataIndex: 'level',
      key: 'level',
      width: 100,
      render: (level) => <Tag color={LEVEL_COLORS[level]}>{LEVEL_LABELS[level]}</Tag>,
    },
    {
      title: 'Asset Count',
      dataIndex: 'assetCount',
      key: 'assetCount',
      width: 100,
      align: 'right',
    },
    {
      title: `Individual Area (${unitLabel})`,
      key: 'individualArea',
      width: 140,
      align: 'right',
      render: (_, record) => {
        if (record.individualAreaSqCm == null) return '-';
        if (record.dimensionStatus === 'missing') {
          return (
            <Tooltip title="Dimensions not set — go to Dimension Setup tab">
              <Icon type="warning" style={{ color: '#faad14', marginRight: 4 }} />
              <span style={{ color: '#bbb' }}>0.00</span>
            </Tooltip>
          );
        }
        return fmtArea(record.individualAreaSqCm, displayUnit);
      },
    },
    {
      title: `Total Area (${unitLabel})`,
      key: 'totalArea',
      width: 140,
      align: 'right',
      sorter: (a, b) => a.totalAreaSqCm - b.totalAreaSqCm,
      render: (_, record) => {
        const val = fmtArea(record.totalAreaSqCm, displayUnit);
        if (record.dimensionStatus === 'partial') {
          return (
            <Tooltip title="Some models in this group have missing dimensions">
              <span>{val}</span>
              <Icon type="warning" style={{ color: '#faad14', marginLeft: 4, fontSize: 12 }} />
            </Tooltip>
          );
        }
        return val;
      },
    },
    {
      title: 'Dimensions',
      key: 'dimensions',
      width: 140,
      render: (_, record) => record.dimensionText || '-',
    },
    {
      title: 'Status',
      key: 'status',
      width: 80,
      align: 'center',
      render: (_, record) => {
        if (record.dimensionStatus === 'set') return <Icon type="check-circle" style={{ color: '#52c41a' }} />;
        if (record.dimensionStatus === 'partial') return <Icon type="exclamation-circle" style={{ color: '#faad14' }} />;
        return <Icon type="close-circle" style={{ color: '#ff4d4f' }} />;
      },
    },
    {
      title: 'Monthly Rent',
      key: 'rent',
      width: 120,
      align: 'right',
      render: (_, record) => {
        if (record.rent == null) return '-';
        return `₹ ${record.rent.toLocaleString('en-IN')}`;
      },
    },
    {
      title: 'Rate / sq ft',
      key: 'ratePerSqFt',
      width: 120,
      align: 'right',
      render: (_, record) => {
        if (record.ratePerSqFt == null) return '-';
        return `₹ ${record.ratePerSqFt.toFixed(2)}`;
      },
    },
  ];

  return (
    <Spin spinning={loading}>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane
          tab={<span><Icon type="tool" /> Dimension Setup</span>}
          key="dimensions"
        >
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Card size="small">
                <div style={{ fontSize: 24, fontWeight: 600 }}>{dimSetupStats.total}</div>
                <div style={{ color: '#999' }}>Total Models</div>
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <div style={{ fontSize: 24, fontWeight: 600, color: '#52c41a' }}>{dimSetupStats.withDims}</div>
                <div style={{ color: '#999' }}>With Dimensions</div>
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <div style={{ fontSize: 24, fontWeight: 600, color: dimSetupStats.missing > 0 ? '#faad14' : '#52c41a' }}>{dimSetupStats.missing}</div>
                <div style={{ color: '#999' }}>Missing Dimensions</div>
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <div style={{ marginBottom: 4, color: '#999' }}>Coverage</div>
                <Progress
                  percent={dimSetupStats.total > 0 ? Math.round((dimSetupStats.withDims / dimSetupStats.total) * 100) : 0}
                  size="small"
                  status={dimSetupStats.missing > 0 ? 'active' : 'success'}
                />
              </Card>
            </Col>
          </Row>

          <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
            <Button icon="download" onClick={handleExportDimensions}>Export Dimensions CSV</Button>
            <Button icon="upload" onClick={() => document.getElementById('dim-csv-import').click()}>Import Dimensions CSV</Button>
            <input
              id="dim-csv-import"
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleImportDimensions}
            />
          </div>

          <Card size="small" style={{ marginBottom: 16 }} title="Filters">
            <Row gutter={16}>
              <Col span={6}>
                <label style={{ display: 'block', marginBottom: 8 }}>Search Model:</label>
                <Input
                  placeholder="Search by name or code"
                  allowClear
                  value={dimFilterSearch}
                  onChange={(e) => setDimFilterSearch(e.target.value)}
                  prefix={<Icon type="search" />}
                />
              </Col>
              <Col span={6}>
                <label style={{ display: 'block', marginBottom: 8 }}>Asset Type:</label>
                <Select
                  style={{ width: '100%' }}
                  placeholder="All Asset Types"
                  allowClear
                  value={dimFilterType}
                  onChange={(v) => { setDimFilterType(v); setDimFilterSubType(null); }}
                  showSearch
                  filterOption={(input, option) => (option.props.children || '').toLowerCase().includes(input.toLowerCase())}
                >
                  {dimTypeOptions.map((t) => <Option key={t} value={t}>{t}</Option>)}
                </Select>
              </Col>
              <Col span={6}>
                <label style={{ display: 'block', marginBottom: 8 }}>Asset Sub-Type:</label>
                <Select
                  style={{ width: '100%' }}
                  placeholder="All Sub-Types"
                  allowClear
                  value={dimFilterSubType}
                  onChange={setDimFilterSubType}
                  showSearch
                  filterOption={(input, option) => (option.props.children || '').toLowerCase().includes(input.toLowerCase())}
                >
                  {dimSubTypeOptions.map((s) => <Option key={s} value={s}>{s}</Option>)}
                </Select>
              </Col>
              <Col span={6}>
                <label style={{ display: 'block', marginBottom: 8 }}>Display Unit:</label>
                <Select value={displayUnit} onChange={setDisplayUnit} style={{ width: '100%' }}>
                  {Object.entries(DISPLAY_UNIT_LABELS).map(([val, label]) => (
                    <Option key={val} value={val}>{label}</Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </Card>

          <Card size="small" style={{ marginBottom: 16 }} title={`Apply to All Filtered Models (${filteredDimModels.length})`}>
            <Row gutter={16} type="flex" align="bottom">
              <Col span={5}>
                <label style={{ display: 'block', marginBottom: 8 }}>Length:</label>
                <InputNumber min={0.01} step={0.1} value={bulkLength} onChange={setBulkLength} style={{ width: '100%' }} placeholder="Length" />
              </Col>
              <Col span={5}>
                <label style={{ display: 'block', marginBottom: 8 }}>Breadth:</label>
                <InputNumber min={0.01} step={0.1} value={bulkBreadth} onChange={setBulkBreadth} style={{ width: '100%' }} placeholder="Breadth" />
              </Col>
              <Col span={4}>
                <label style={{ display: 'block', marginBottom: 8 }}>Unit:</label>
                <Select value={bulkUnit} onChange={setBulkUnit} style={{ width: '100%' }}>
                  <Option value="cm">cm</Option>
                  <Option value="inches">inches</Option>
                  <Option value="ft">ft</Option>
                </Select>
              </Col>
              <Col span={5}>
                <Popconfirm
                  title={`This will set dimensions for ${filteredDimModels.length} model(s). Existing dimensions will be overwritten. Continue?`}
                  onConfirm={handleBulkApply}
                  okText="Apply"
                  cancelText="Cancel"
                >
                  <Button
                    type="primary"
                    icon="edit"
                    disabled={!bulkLength || !bulkBreadth || filteredDimModels.length === 0}
                  >
                    Apply to {filteredDimModels.length} Model(s)
                  </Button>
                </Popconfirm>
              </Col>
            </Row>
          </Card>

          <Table
            columns={dimColumns}
            dataSource={filteredDimModels}
            rowKey="_id"
            pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (total) => `Total ${total} models` }}
            scroll={{ x: 1000 }}
            size="small"
          />
        </TabPane>

        <TabPane
          tab={<span><Icon type="home" /> Parking Rent</span>}
          key="rent"
        >
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Card size="small">
                <div style={{ fontSize: 24, fontWeight: 600 }}>{rentStats.total}</div>
                <div style={{ color: '#999' }}>Total Locations</div>
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <div style={{ fontSize: 24, fontWeight: 600, color: '#52c41a' }}>{rentStats.withRent}</div>
                <div style={{ color: '#999' }}>With Rent</div>
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <div style={{ fontSize: 24, fontWeight: 600, color: rentStats.missing > 0 ? '#faad14' : '#52c41a' }}>{rentStats.missing}</div>
                <div style={{ color: '#999' }}>Missing Rent</div>
              </Card>
            </Col>
          </Row>

          <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
            <Button icon="download" onClick={handleExportRent}>Export Rent CSV</Button>
            <Button icon="upload" onClick={() => document.getElementById('rent-csv-import').click()}>Import Rent CSV</Button>
            <input
              id="rent-csv-import"
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleImportRent}
            />
          </div>

          <Card size="small" style={{ marginBottom: 16 }} title="Search">
            <Input
              placeholder="Search by location or sub-location name"
              allowClear
              value={rentFilterSearch}
              onChange={(e) => setRentFilterSearch(e.target.value)}
              prefix={<Icon type="search" />}
              style={{ width: 400 }}
            />
          </Card>

          <Table
            dataSource={filteredRentList}
            rowKey="key"
            pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (total) => `Total ${total} locations` }}
            size="small"
            columns={[
              {
                title: 'Location',
                dataIndex: 'locationName',
                key: 'locationName',
                width: 200,
                sorter: (a, b) => a.locationName.localeCompare(b.locationName),
              },
              {
                title: 'Sub-Location',
                dataIndex: 'subLocationName',
                key: 'subLocationName',
                width: 200,
                sorter: (a, b) => a.subLocationName.localeCompare(b.subLocationName),
              },
              {
                title: 'Assets',
                key: 'assetCount',
                width: 80,
                render: (_, record) => normalizedAssets.filter((a) => a.locationName === record.locationName && a.subLocationName === record.subLocationName).length,
              },
              {
                title: 'Monthly Rent (₹)',
                key: 'rent',
                width: 160,
                render: (_, record) => {
                  if (editingRentKey === record.key) {
                    return <InputNumber min={0.01} step={100} value={editRentAmount} onChange={setEditRentAmount} style={{ width: 130 }} />;
                  }
                  const data = parkingRent[record.key];
                  return data && data.rent > 0
                    ? `₹ ${data.rent.toLocaleString('en-IN')}`
                    : <span style={{ color: '#bbb' }}>-</span>;
                },
              },
              {
                title: 'Actions',
                key: 'actions',
                width: 150,
                render: (_, record) => {
                  if (editingRentKey === record.key) {
                    return (
                      <span>
                        <Button type="primary" size="small" icon="save" onClick={handleSaveRent} style={{ marginRight: 4 }}>Save</Button>
                        <Button size="small" onClick={handleCancelRentEdit}>Cancel</Button>
                      </span>
                    );
                  }
                  const hasRent = parkingRent[record.key] && parkingRent[record.key].rent > 0;
                  return (
                    <span>
                      <Button type="link" size="small" icon="edit" onClick={() => handleEditRent(record.key)}>
                        {hasRent ? 'Edit' : 'Set'}
                      </Button>
                      {hasRent && (
                        <Popconfirm title="Remove rent for this location?" onConfirm={() => handleDeleteRent(record.key)}>
                          <Button type="link" size="small" icon="delete" style={{ color: '#ff4d4f' }} />
                        </Popconfirm>
                      )}
                    </span>
                  );
                },
              },
            ]}
          />
        </TabPane>

        <TabPane
          tab={<span><Icon type="area-chart" /> Space Report</span>}
          key="report"
        >
          <Card
            size="small"
            style={{ marginBottom: 16 }}
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Filters</span>
                <Button
                  type="link"
                  size="small"
                  icon={filtersVisible ? 'up' : 'down'}
                  onClick={() => setFiltersVisible(!filtersVisible)}
                  style={{ padding: 0, height: 'auto' }}
                >
                  {filtersVisible ? 'Hide' : 'Show'} Filters
                </Button>
              </div>
            }
          >
            {filtersVisible && (
              <>
                <Row gutter={16}>
                  <Col span={8}>
                    <label style={{ display: 'block', marginBottom: 8 }}>Location:</label>
                    <Select
                      style={{ width: '100%' }}
                      placeholder="All Locations"
                      allowClear
                      value={fLocation}
                      onChange={(v) => { setFLocation(v); setFSubLocation(null); }}
                      showSearch
                      filterOption={(input, option) => (option.props.children || '').toLowerCase().includes(input.toLowerCase())}
                    >
                      {locationOptions.map((loc) => <Option key={loc} value={loc}>{loc}</Option>)}
                    </Select>
                  </Col>
                  <Col span={8}>
                    <label style={{ display: 'block', marginBottom: 8 }}>Sub-Location:</label>
                    <Select
                      style={{ width: '100%' }}
                      placeholder="All Sub-Locations"
                      allowClear
                      value={fSubLocation}
                      onChange={setFSubLocation}
                      showSearch
                      filterOption={(input, option) => (option.props.children || '').toLowerCase().includes(input.toLowerCase())}
                    >
                      {subLocationOptions.map((s) => <Option key={s} value={s}>{s}</Option>)}
                    </Select>
                  </Col>
                  <Col span={8}>
                    <label style={{ display: 'block', marginBottom: 8 }}>Asset Type:</label>
                    <Select
                      style={{ width: '100%' }}
                      placeholder="All Asset Types"
                      allowClear
                      value={fAssetType}
                      onChange={(v) => { setFAssetType(v); setFAssetSubType(null); setFModel(null); }}
                      showSearch
                      filterOption={(input, option) => (option.props.children || '').toLowerCase().includes(input.toLowerCase())}
                    >
                      {assetTypeOptions.map((t) => <Option key={t} value={t}>{t}</Option>)}
                    </Select>
                  </Col>
                </Row>
                <Row gutter={16} style={{ marginTop: 16 }}>
                  <Col span={8}>
                    <label style={{ display: 'block', marginBottom: 8 }}>Asset Sub-Type:</label>
                    <Select
                      style={{ width: '100%' }}
                      placeholder="All Sub-Types"
                      allowClear
                      value={fAssetSubType}
                      onChange={(v) => { setFAssetSubType(v); setFModel(null); }}
                      showSearch
                      filterOption={(input, option) => (option.props.children || '').toLowerCase().includes(input.toLowerCase())}
                    >
                      {assetSubTypeOptions.map((s) => <Option key={s} value={s}>{s}</Option>)}
                    </Select>
                  </Col>
                  <Col span={8}>
                    <label style={{ display: 'block', marginBottom: 8 }}>Model:</label>
                    <Select
                      style={{ width: '100%' }}
                      placeholder="All Models"
                      allowClear
                      value={fModel}
                      onChange={setFModel}
                      showSearch
                      filterOption={(input, option) => (option.props.children || '').toLowerCase().includes(input.toLowerCase())}
                    >
                      {modelOptions.map((m) => <Option key={m} value={m}>{m}</Option>)}
                    </Select>
                  </Col>
                  <Col span={8}>
                    <label style={{ display: 'block', marginBottom: 8 }}>Owner:</label>
                    <Select
                      style={{ width: '100%' }}
                      placeholder="All Owners"
                      allowClear
                      value={fOwner}
                      onChange={setFOwner}
                      showSearch
                      filterOption={(input, option) => (option.props.children || '').toLowerCase().includes(input.toLowerCase())}
                    >
                      {ownerOptions.map((o) => <Option key={o} value={o}>{o}</Option>)}
                    </Select>
                  </Col>
                </Row>
                <Row gutter={16} style={{ marginTop: 16 }}>
                  <Col span={8}>
                    <label style={{ display: 'block', marginBottom: 8 }}>Display Unit:</label>
                    <Select value={displayUnit} onChange={setDisplayUnit} style={{ width: '100%' }}>
                      {Object.entries(DISPLAY_UNIT_LABELS).map(([val, label]) => (
                        <Option key={val} value={val}>{label}</Option>
                      ))}
                    </Select>
                  </Col>
                  <Col span={8} style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
                    <Checkbox checked={onlyWithRent} onChange={(e) => setOnlyWithRent(e.target.checked)}>Only locations with rent</Checkbox>
                    <Button icon="reload" onClick={resetFilters} style={{ marginBottom: 0 }}>Reset Filters</Button>
                  </Col>
                </Row>
              </>
            )}
          </Card>

          {summaryStats.modelsMissing > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`${summaryStats.modelsMissing} of ${summaryStats.modelsInReport} model(s) in this report have no dimensions configured. Their area is counted as zero. Set dimensions in the Dimension Setup tab.`}
              style={{ marginBottom: 16 }}
            />
          )}

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Card size="small">
                <div style={{ fontSize: 24, fontWeight: 600 }}>{summaryStats.totalAssets}</div>
                <div style={{ color: '#999' }}>Total Assets</div>
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <div style={{ fontSize: 24, fontWeight: 600, color: '#1890ff' }}>{summaryStats.totalArea}</div>
                <div style={{ color: '#999' }}>Total Area ({unitLabel})</div>
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <div style={{ fontSize: 24, fontWeight: 600, color: '#52c41a' }}>{summaryStats.modelsWithDims}</div>
                <div style={{ color: '#999' }}>Models with Dimensions</div>
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <div style={{ fontSize: 24, fontWeight: 600, color: summaryStats.modelsMissing > 0 ? '#faad14' : '#52c41a' }}>{summaryStats.modelsMissing}</div>
                <div style={{ color: '#999' }}>Models Missing Dimensions</div>
              </Card>
            </Col>
          </Row>

          <Card
            size="small"
            extra={
              <span>
                <Button icon="download" size="small" onClick={handleDownloadCSV} style={{ marginRight: 8 }} disabled={reportData.length === 0}>CSV</Button>
                <Button icon="file-pdf" size="small" onClick={handleDownloadPDF} disabled={reportData.length === 0}>PDF</Button>
              </span>
            }
            title={`Space Breakdown by Location (${unitLabel})`}
          >
            <Table
              columns={reportColumns}
              dataSource={reportData}
              rowKey="key"
              defaultExpandAllRows={false}
              defaultExpandedRowKeys={reportData.map((n) => n.key)}
              pagination={false}
              scroll={{ x: 1400 }}
              size="small"
            />
          </Card>
        </TabPane>
      </Tabs>
    </Spin>
  );
}

export default SpaceUtilizationReport;
